import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useApplicantsMerge } from "@/queries/recruit/use-applicants.query.ts";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCFEntityTypes, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiApplicant, ApiApplicantShort } from "@/types/recruit/applicant.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { Button, ListItem, Modal, ModalProps, Stack, Text } from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface ApplicantIgnoredModalProps extends ModalProps {
  applicant: ApiApplicant;
}

export function ApplicantIgnoredModal({ applicant, ...props }: ApplicantIgnoredModalProps) {
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const { hasField } = useEntityFormConfig(ApiCFEntityTypes.applicant);
  const applicantsMerge = useApplicantsMerge();

  const [returnedIds, setReturnedIds] = useState<number[]>([]);

  const canManageCandidate = canIEdit(ApiPermission.manage_candidates);
  // Backend merge copies the applicant's own id into its ignore_ids
  const ignoredApplicants = applicant.ignored_applicants.filter(
    (item) => item.id !== applicant.id && !returnedIds.includes(item.id)
  );

  const handleUnignore = async (ignoredApplicant: ApiApplicantShort) => {
    try {
      await applicantsMerge.mutateAsync({
        applicantId: applicant.id,
        similarApplicantId: ignoredApplicant.id,
        resolveType: "unignore",
      });
      analyticsService.trackEvent(analyticEvents.applicants.mergeResolved, {
        [analyticProperties.id]: applicant.id,
        [analyticProperties.actionType]: "unignore",
      });
      setReturnedIds((ids) => [...ids, ignoredApplicant.id]);
    } catch {
      return;
    }
  };

  return (
    <Modal {...props} size={"lg"} title={t("ats:rejected_duplicates")} withCloser closeByEscEnabled>
      <Stack gap={"lg"}>
        <Text className={"text-secondary"}>
          {t("ats:rejected_duplicates_description")}
          {canManageCandidate && ` ${t("ats:rejected_duplicates_return_hint")}`}
        </Text>
        {ignoredApplicants.length === 0 ? (
          <Text className={"text-secondary"}>{t("ats:no_rejected_duplicates")}</Text>
        ) : (
          <Stack>
            {ignoredApplicants.map((item) => (
              <ListItem
                key={`ignored-applicant-${item.id}`}
                avatarUrl={item.photo_url}
                forceShowAvatar
                title={`${item.first_name} ${item.last_name}`}
                caption={[
                  hasField(ApiFieldTypes.applicant_position)
                    ? item.current_position.position
                    : undefined,
                  hasField(ApiFieldTypes.applicant_company)
                    ? item.current_position.company
                    : undefined,
                ]
                  .filter(Boolean)
                  .join(", ")}
                behavior={"list"}
                valueSlot={
                  canManageCandidate && (
                    <Button
                      variant={"secondary"}
                      size={"sm"}
                      isLoading={
                        applicantsMerge.isPending &&
                        applicantsMerge.variables.similarApplicantId === item.id
                      }
                      disabled={applicantsMerge.isPending}
                      onClick={() => handleUnignore(item)}
                    >
                      {t("ats:return_to_duplicates")}
                    </Button>
                  )
                }
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
